import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Trophy, TrendingUp, RotateCcw, Home, FileText, Award, Target } from 'lucide-react';

const WrittenPracticeResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scores, totalObtained, totalPossible, percentage, passed, config, questions } = location.state || {};

  if (!scores || percentage === undefined) {
    navigate('/practice');
    return null;
  }

  const fullMarksCount = scores.filter(s => s.marks_given === s.max_marks).length;
  const zeroMarksCount = scores.filter(s => s.marks_given === 0).length;
  const partialMarksCount = scores.length - fullMarksCount - zeroMarksCount;

  // Get grade based on percentage
  const getGrade = (pct) => {
    if (pct >= 90) return { grade: 'A+', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (pct >= 80) return { grade: 'A', color: 'text-green-600', bg: 'bg-green-100' };
    if (pct >= 70) return { grade: 'B', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (pct >= 60) return { grade: 'C', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (pct >= 50) return { grade: 'D', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { grade: 'F', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const gradeInfo = getGrade(percentage);

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto">
        {/* Results Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0A7EA4]/10 rounded-full mb-4">
            {passed ? (
              <Trophy className="w-10 h-10 text-[#10B981]" />
            ) : (
              <TrendingUp className="w-10 h-10 text-orange-500" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Written Practice Complete!
          </h1>
          <div className="flex items-center justify-center gap-2">
            <Badge className="bg-[#0A7EA4]">
              <FileText className="w-3 h-3 mr-1" />
              Written Questions
            </Badge>
          </div>
        </div>

        {/* Main Score Card */}
        <Card className="mb-6 border-t-4 border-t-[#0A7EA4]">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              {/* Marks */}
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  {totalObtained} / {totalPossible}
                </div>
                <p className="text-sm text-gray-500">Total Marks</p>
              </div>
              
              {/* Percentage */}
              <div className="text-center border-x border-gray-200">
                <div className={`text-5xl font-bold mb-1 ${
                  passed ? 'text-[#10B981]' : 'text-orange-500'
                }`}>
                  {percentage.toFixed(1)}%
                </div>
                <Badge className={`text-sm px-3 py-1 ${
                  passed 
                    ? 'bg-[#10B981] hover:bg-[#10B981]/90' 
                    : 'bg-orange-500 hover:bg-orange-600'
                }`}>
                  {passed ? '✓ Passed' : 'Keep Practicing'}
                </Badge>
              </div>
              
              {/* Grade */}
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${gradeInfo.bg} mb-1`}>
                  <span className={`text-3xl font-bold ${gradeInfo.color}`}>{gradeInfo.grade}</span>
                </div>
                <p className="text-sm text-gray-500">Grade</p>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-gray-600">
                {passed 
                  ? 'Great job! You\'ve achieved a passing score. Keep up the good work!' 
                  : 'You need 60% or above to pass. Review your answers and keep practicing!'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <Target className="w-3 h-3" />
                Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#0A7EA4]">{scores.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <Award className="w-3 h-3 text-green-600" />
                Full Marks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{fullMarksCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500">Partial Marks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{partialMarksCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500">Zero Marks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{zeroMarksCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Question Breakdown */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Question Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {scores.map((score, index) => {
                const question = questions?.[index];
                const pct = score.max_marks > 0 ? (score.marks_given / score.max_marks) * 100 : 0;
                
                return (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          Q{index + 1}
                        </span>
                        {pct >= 100 && (
                          <Badge className="bg-green-600 text-xs">Full</Badge>
                        )}
                        {pct > 0 && pct < 100 && (
                          <Badge className="bg-amber-500 text-xs">Partial</Badge>
                        )}
                        {pct === 0 && (
                          <Badge className="bg-red-500 text-xs">Zero</Badge>
                        )}
                      </div>
                      {question && (
                        <p className="text-xs text-gray-500 truncate mt-1">
                          {question.question_text?.substring(0, 60)}...
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-gray-900">
                        {score.marks_given}/{score.max_marks}
                      </div>
                      <div className={`text-xs ${
                        pct >= 60 ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {pct.toFixed(0)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Performance Tips */}
        <Card className="mb-6 bg-gradient-to-r from-cyan-50 to-blue-50">
          <CardHeader>
            <CardTitle className="text-[#0A7EA4]">Performance Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-700">
              {percentage < 60 && (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-[#0A7EA4] mt-1">•</span>
                    <span>Review the model answers for questions where you scored low</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#0A7EA4] mt-1">•</span>
                    <span>Focus on understanding key concepts and exam patterns</span>
                  </li>
                </>  
              )}
              {zeroMarksCount > 0 && (
                <li className="flex items-start gap-2">
                  <span className="text-[#0A7EA4] mt-1">•</span>
                  <span>Try to attempt all questions - even partial answers can score marks</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <span className="text-[#0A7EA4] mt-1">•</span>
                <span>Practice writing answers within the expected mark-wise structure</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0A7EA4] mt-1">•</span>
                <span>Use diagrams and bullet points where applicable for better presentation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0A7EA4] mt-1">•</span>
                <span>Browse the Written Question Bank to study more questions from past papers</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid md:grid-cols-3 gap-4">
          <Button
            onClick={() => navigate('/practice')}
            className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90 flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            New Practice Session
          </Button>
          <Button
            onClick={() => navigate('/written-bank')}
            variant="outline"
            className="flex items-center justify-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Written Question Bank
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default WrittenPracticeResultsPage;
